<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['import']);
const { t } = useI18n();
const store = useStore();

const SYSTEM_FIELDS = [
  { key: 'name', label: 'Nome' },
  { key: 'email', label: 'E-mail' },
  { key: 'phone_number', label: 'Telefone' },
  { key: 'identifier', label: 'Identificador' },
  { key: 'company_name', label: 'Empresa' },
  { key: 'city', label: 'Cidade' },
  { key: 'labels', label: 'Labels' },
  { key: 'cpf', label: 'CPF' },
  { key: 'matricula', label: 'Matrícula' },
  { key: 'banco', label: 'Banco' },
  { key: 'data_nascimento', label: 'Data de Nascimento' },
  { key: 'convenio', label: 'Convênio' },
  { key: 'margem', label: 'Margem' },
  { key: 'valor_parcela', label: 'Valor Parcela' },
  { key: 'prazo', label: 'Prazo' },
  { key: 'observacao', label: 'Observação' },
];

const uiFlags = useMapGetter('contacts/getUIFlags');
const isImportingContact = computed(() => uiFlags.value.isImporting);
const accountLabels = useMapGetter('labels/getLabels');

const dialogRef = ref(null);
const fileInput = ref(null);

const currentStep = ref(1);
const selectedFile = ref(null);
const selectedFileName = ref('');
const csvHeaders = ref([]);
const csvPreview = ref([]);
const columnMapping = ref({});
const selectedLabels = ref([]);
const importId = ref(null);
const importProgress = ref(null);
const progressInterval = ref(null);
const importError = ref('');

const dialogTitle = computed(() => {
  if (currentStep.value === 1) return 'Importar Contatos';
  if (currentStep.value === 2) return 'Mapear Colunas';
  if (currentStep.value === 3) {
    if (importProgress.value?.status === 'completed' || importProgress.value?.status === 'completed_with_errors')
      return 'Importação Concluída';
    return 'Importando...';
  }
  return 'Importar Contatos';
});

const showConfirmButton = computed(() => {
  if (currentStep.value === 1) return !!selectedFile.value;
  if (currentStep.value === 2) return hasValidMapping.value;
  return false;
});

const confirmLabel = computed(() => {
  if (currentStep.value === 1) return 'Avançar';
  if (currentStep.value === 2) return 'Iniciar Importação';
  return '';
});

const hasValidMapping = computed(() => {
  const values = Object.values(columnMapping.value);
  const mapped = values.filter(v => v && v !== '_skip');
  return mapped.length > 0;
});

const availableLabels = computed(() => {
  return (accountLabels.value || []).filter(l => !selectedLabels.value.includes(l.title));
});

const addLabel = (event) => {
  const val = event.target.value;
  if (val && !selectedLabels.value.includes(val)) {
    selectedLabels.value.push(val);
  }
  event.target.value = '';
};

const removeLabel = (label) => {
  selectedLabels.value = selectedLabels.value.filter(l => l !== label);
};

const getLabelColor = (title) => {
  const found = (accountLabels.value || []).find(l => l.title === title);
  return found?.color || '#6366f1';
};

const isFinished = computed(() => {
  const s = importProgress.value?.status;
  return s === 'completed' || s === 'completed_with_errors' || s === 'failed';
});

const importedCount = computed(() => {
  if (!importProgress.value) return 0;
  const total = importProgress.value.processed_records || 0;
  const failed = importProgress.value.failed_records_count || 0;
  return total - failed;
});

const failedCount = computed(() => importProgress.value?.failed_records_count || 0);
const progressPercent = computed(() => importProgress.value?.percent || 0);
const failedRecords = computed(() => importProgress.value?.failed_records_data || []);

const processFileName = fileName => {
  const lastDotIndex = fileName.lastIndexOf('.');
  const extension = fileName.slice(lastDotIndex);
  const baseName = fileName.slice(0, lastDotIndex);
  return baseName.length > 30 ? `${baseName.slice(0, 30)}...${extension}` : fileName;
};

const handleFileClick = () => fileInput.value?.click();

const detectSeparator = text => {
  const firstLine = text.split('\n')[0] || '';
  const semicolons = (firstLine.match(/;/g) || []).length;
  const commas = (firstLine.match(/,/g) || []).length;
  const tabs = (firstLine.match(/\t/g) || []).length;
  if (semicolons > commas && semicolons > tabs) return ';';
  if (tabs > commas) return '\t';
  return ',';
};

const parseCSVLine = (line, sep) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === sep) {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
};

const handleFileChange = () => {
  const file = fileInput.value?.files[0];
  if (!file) return;

  selectedFile.value = file;
  selectedFileName.value = processFileName(file.name);
  importError.value = '';

  const reader = new FileReader();
  reader.onload = e => {
    try {
      let text = e.target.result;
      if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);

      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 1) {
        importError.value = 'O arquivo está vazio ou não contém cabeçalhos.';
        return;
      }

      const sep = detectSeparator(text);
      const headers = parseCSVLine(lines[0], sep);
      csvHeaders.value = headers;

      const previewRows = [];
      for (let i = 1; i < Math.min(lines.length, 4); i++) {
        const vals = parseCSVLine(lines[i], sep);
        const row = {};
        headers.forEach((h, idx) => {
          row[h] = vals[idx] || '';
        });
        previewRows.push(row);
      }
      csvPreview.value = previewRows;

      const ALIASES = {
        name: ['nome', 'name', 'nome_completo'],
        email: ['email', 'e-mail', 'e_mail', 'mail'],
        phone_number: ['telefone', 'celular', 'fone', 'tel', 'whatsapp', 'phone', 'phone_number', 'numero'],
        identifier: ['identifier', 'identificador', 'id'],
        company_name: ['company_name', 'empresa', 'company', 'nome_empresa'],
        city: ['city', 'cidade'],
        labels: ['labels', 'tags', 'etiquetas'],
        cpf: ['cpf', 'documento', 'doc'],
        matricula: ['matricula', 'matrícula', 'mat'],
        banco: ['banco', 'bank'],
        data_nascimento: ['data_nascimento', 'nascimento', 'data nascimento', 'birthday', 'birth_date'],
        convenio: ['convenio', 'convênio', 'convenio_nome'],
        margem: ['margem', 'margem_livre', 'margin'],
        valor_parcela: ['valor_parcela', 'parcela', 'valor parcela'],
        prazo: ['prazo', 'prazo_maximo'],
        observacao: ['observacao', 'observação', 'obs', 'nota', 'note'],
      };

      const normalize = str => str.toLowerCase().trim()
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9_]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');

      const mapping = {};
      headers.forEach(header => {
        const n = normalize(header);
        let matched = '_skip';

        for (const [field, aliases] of Object.entries(ALIASES)) {
          if (aliases.some(a => normalize(a) === n)) {
            matched = field;
            break;
          }
        }

        mapping[header] = matched;
      });

      columnMapping.value = mapping;
    } catch {
      importError.value = 'Não foi possível ler o arquivo CSV.';
    }
  };
  reader.readAsText(file);
};

const handleRemoveFile = () => {
  selectedFile.value = null;
  csvHeaders.value = [];
  csvPreview.value = [];
  columnMapping.value = {};
  if (fileInput.value) fileInput.value.value = null;
  selectedFileName.value = '';
  importError.value = '';
};

const goToMapping = () => {
  if (!selectedFile.value) return;
  if (csvHeaders.value.length === 0) {
    importError.value = 'Não foi possível detectar colunas no CSV.';
    return;
  }
  store.dispatch('labels/get');
  currentStep.value = 2;
};

const goBack = () => {
  if (currentStep.value === 2) {
    currentStep.value = 1;
  }
};

const startImport = async () => {
  if (!selectedFile.value || !hasValidMapping.value) return;

  const cleanMapping = {};
  Object.entries(columnMapping.value).forEach(([csvCol, sysField]) => {
    if (sysField && sysField !== '_skip') {
      cleanMapping[csvCol] = sysField;
    }
  });

  currentStep.value = 3;
  importProgress.value = { status: 'processing', percent: 0, processed_records: 0, total_records: 0, failed_records_count: 0, failed_records_data: [] };
  importError.value = '';

  try {
    const result = await store.dispatch('contacts/import', {
      file: selectedFile.value,
      columnMapping: cleanMapping,
      globalLabels: selectedLabels.value.join(','),
    });

    importId.value = result.id;
    startProgressPolling();
  } catch (error) {
    importProgress.value = { status: 'failed', percent: 0, processed_records: 0, total_records: 0, failed_records_count: 0, failed_records_data: [] };
    importError.value = error.message || 'Erro ao iniciar importação.';
  }
};

const checkProgress = async () => {
  try {
    const progress = await store.dispatch('contacts/getImportProgress', importId.value);
    importProgress.value = progress;
    if (progress.status === 'completed' || progress.status === 'completed_with_errors' || progress.status === 'failed') {
      stopProgressPolling();
      return true;
    }
  } catch {
    // silently retry
  }
  return false;
};

const startProgressPolling = async () => {
  stopProgressPolling();
  await new Promise(r => setTimeout(r, 800));
  const done = await checkProgress();
  if (done) return;

  progressInterval.value = setInterval(async () => {
    await checkProgress();
  }, 1500);
};

const stopProgressPolling = () => {
  if (progressInterval.value) {
    clearInterval(progressInterval.value);
    progressInterval.value = null;
  }
};

const reprocessErrors = async () => {
  if (!importId.value || failedCount.value === 0) return;

  try {
    const result = await store.dispatch('contacts/reprocessFailed', importId.value);
    importId.value = result.id;
    importProgress.value = { status: 'processing', percent: 0, processed_records: 0, total_records: failedCount.value, failed_records_count: 0, failed_records_data: [] };
    startProgressPolling();
  } catch (error) {
    importError.value = error.response?.data?.error || 'Erro ao reprocessar.';
  }
};

const resetDialog = () => {
  stopProgressPolling();
  currentStep.value = 1;
  selectedFile.value = null;
  selectedFileName.value = '';
  csvHeaders.value = [];
  csvPreview.value = [];
  columnMapping.value = {};
  selectedLabels.value = [];
  importId.value = null;
  importProgress.value = null;
  importError.value = '';
  if (fileInput.value) fileInput.value.value = null;
};

const onConfirm = () => {
  if (currentStep.value === 1) goToMapping();
  else if (currentStep.value === 2) startImport();
};

const onClose = () => {
  resetDialog();
};

onBeforeUnmount(() => {
  stopProgressPolling();
});

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="dialogTitle"
    :confirm-button-label="confirmLabel"
    :is-loading="isImportingContact"
    :disable-confirm-button="isImportingContact || (currentStep === 2 && !hasValidMapping)"
    :show-confirm-button="showConfirmButton"
    :width="currentStep === 2 ? '3xl' : 'xl'"
    :overflow-y-auto="currentStep === 2"
    @confirm="onConfirm"
    @close="onClose"
  >
    <!-- STEP 1: Upload -->
    <div v-if="currentStep === 1" class="flex flex-col gap-3">
      <p class="text-sm text-n-slate-11">
        Selecione qualquer arquivo CSV. Na próxima etapa você vai mapear as colunas
        para os campos do sistema.
      </p>

      <div class="flex items-center gap-2">
        <label class="text-sm text-n-slate-12 whitespace-nowrap">Arquivo CSV:</label>
        <div class="flex items-center justify-between w-full gap-2">
          <span v-if="selectedFile" class="text-sm text-n-slate-12">
            {{ selectedFileName }}
          </span>
          <Button
            v-if="!selectedFile"
            label="Escolher arquivo"
            icon="i-lucide-upload"
            color="slate"
            variant="ghost"
            size="sm"
            class="!w-fit"
            @click="handleFileClick"
          />
          <div v-else class="flex items-center gap-1">
            <Button
              label="Trocar"
              color="slate"
              variant="ghost"
              size="sm"
              @click="handleFileClick"
            />
            <div class="w-px h-3 bg-n-strong" />
            <Button
              icon="i-lucide-trash"
              color="slate"
              variant="ghost"
              size="sm"
              @click="handleRemoveFile"
            />
          </div>
        </div>
      </div>

      <div v-if="csvHeaders.length > 0" class="rounded-lg border border-n-weak p-3 bg-n-alpha-1">
        <p class="text-xs text-n-slate-11 mb-1">
          {{ csvHeaders.length }} colunas detectadas · {{ csvPreview.length }} linhas de preview
        </p>
        <div class="flex flex-wrap gap-1">
          <span
            v-for="h in csvHeaders"
            :key="h"
            class="inline-flex items-center px-2 py-0.5 rounded-md bg-n-alpha-2 text-xs text-n-slate-12"
          >
            {{ h }}
          </span>
        </div>
      </div>

      <p v-if="importError" class="text-sm text-n-ruby-11">{{ importError }}</p>

      <input
        ref="fileInput"
        type="file"
        accept=".csv,.txt,.tsv"
        class="hidden"
        @change="handleFileChange"
      />
    </div>

    <!-- STEP 2: Mapping -->
    <div v-if="currentStep === 2" class="flex flex-col gap-3">
      <div class="flex items-center justify-between">
        <Button
          label="Voltar"
          icon="i-lucide-arrow-left"
          color="slate"
          variant="ghost"
          size="sm"
          @click="goBack"
        />
        <p class="text-xs text-n-slate-11">
          Mapeie cada coluna do CSV para um campo do sistema. Colunas não mapeadas serão ignoradas.
        </p>
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">Tags para aplicar em todos os contatos</label>
        <div class="flex flex-wrap gap-1.5 min-h-[36px] rounded-lg border border-n-weak bg-n-solid-2 px-2.5 py-2">
          <span
            v-for="label in selectedLabels"
            :key="label"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium text-white"
            :style="{ backgroundColor: getLabelColor(label) }"
          >
            {{ label }}
            <button
              type="button"
              class="ml-0.5 hover:opacity-70"
              @click="removeLabel(label)"
            >&times;</button>
          </span>
          <select
            class="flex-1 min-w-[140px] bg-transparent border-0 text-sm text-n-slate-12 focus:outline-none cursor-pointer"
            @change="addLabel($event)"
          >
            <option value="">Selecionar tag...</option>
            <option
              v-for="label in availableLabels"
              :key="label.id"
              :value="label.title"
            >
              {{ label.title }}
            </option>
          </select>
        </div>
        <p class="text-xs text-n-slate-10">Opcional. Selecione tags do sistema para aplicar em todos os contatos importados.</p>
      </div>

      <div class="rounded-xl border border-n-weak overflow-hidden max-h-[50vh] overflow-y-auto">
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="bg-n-alpha-1 border-b border-n-weak">
                <th class="px-3 py-2 text-left text-xs font-medium text-n-slate-11 w-1/3">Coluna CSV</th>
                <th class="px-3 py-2 text-left text-xs font-medium text-n-slate-11 w-1/3">Campo no Sistema</th>
                <th class="px-3 py-2 text-left text-xs font-medium text-n-slate-11 w-1/3">Preview</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="header in csvHeaders"
                :key="header"
                class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1 transition-colors"
              >
                <td class="px-3 py-2">
                  <span class="font-medium text-n-slate-12">{{ header }}</span>
                </td>
                <td class="px-3 py-2">
                  <select
                    v-model="columnMapping[header]"
                    class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-2.5 py-1.5 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
                  >
                    <option value="_skip">— Ignorar —</option>
                    <option
                      v-for="field in SYSTEM_FIELDS"
                      :key="field.key"
                      :value="field.key"
                    >
                      {{ field.label }}
                    </option>
                  </select>
                </td>
                <td class="px-3 py-2">
                  <span class="text-xs text-n-slate-10 truncate block max-w-[200px]">
                    {{ csvPreview[0]?.[header] || '—' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- STEP 3: Progress / Summary -->
    <div v-if="currentStep === 3" class="flex flex-col gap-4">
      <!-- Progress bar -->
      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ isFinished ? 'Concluído' : (importProgress?.status === 'pending' || (!importProgress?.total_records && !isFinished) ? 'Iniciando processamento...' : 'Processando...') }}
          </span>
          <span v-if="importProgress?.total_records" class="text-sm tabular-nums text-n-slate-11">{{ progressPercent }}%</span>
        </div>
        <div class="h-3 w-full overflow-hidden rounded-full bg-n-alpha-2">
          <div
            class="h-full rounded-full transition-all duration-500 ease-out"
            :class="[
              importProgress?.status === 'failed' ? 'bg-n-ruby-9' :
              importProgress?.status === 'completed_with_errors' ? 'bg-n-amber-9' :
              'bg-n-brand'
            ]"
            :style="{ width: `${progressPercent}%` }"
          />
        </div>
        <p class="text-xs text-n-slate-10">
          {{ importProgress?.processed_records || 0 }} de {{ importProgress?.total_records || 0 }} registros processados
        </p>
      </div>

      <!-- Summary (when finished) -->
      <div v-if="isFinished" class="flex flex-col gap-3">
        <div class="grid grid-cols-2 gap-3">
          <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4 flex flex-col items-center gap-1">
            <span class="text-2xl font-bold tabular-nums text-n-jade-11">{{ importedCount }}</span>
            <span class="text-xs text-n-slate-11">Importados com sucesso</span>
          </div>
          <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4 flex flex-col items-center gap-1">
            <span
              class="text-2xl font-bold tabular-nums"
              :class="failedCount > 0 ? 'text-n-ruby-11' : 'text-n-slate-11'"
            >{{ failedCount }}</span>
            <span class="text-xs text-n-slate-11">Com erro</span>
          </div>
        </div>

        <!-- Failed records table -->
        <div v-if="failedRecords.length > 0" class="flex flex-col gap-2">
          <h4 class="text-sm font-medium text-n-slate-12">Registros com erro:</h4>
          <div class="rounded-xl border border-n-weak overflow-hidden max-h-48 overflow-y-auto">
            <table class="w-full text-xs">
              <thead class="sticky top-0">
                <tr class="bg-n-alpha-1 border-b border-n-weak">
                  <th class="px-2 py-1.5 text-left font-medium text-n-slate-11">#</th>
                  <th class="px-2 py-1.5 text-left font-medium text-n-slate-11">Dados</th>
                  <th class="px-2 py-1.5 text-left font-medium text-n-slate-11">Erro</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(record, idx) in failedRecords"
                  :key="idx"
                  class="border-b border-n-weak last:border-0"
                >
                  <td class="px-2 py-1.5 text-n-slate-10">{{ idx + 1 }}</td>
                  <td class="px-2 py-1.5 text-n-slate-12 max-w-[200px] truncate">
                    {{ Object.entries(record).filter(([k]) => k !== 'error').map(([k, v]) => `${k}: ${v}`).join(', ') }}
                  </td>
                  <td class="px-2 py-1.5 text-n-ruby-11">{{ record.error }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <Button
            label="Reprocessar erros"
            icon="i-lucide-refresh-cw"
            color="slate"
            variant="faded"
            size="sm"
            class="self-start"
            @click="reprocessErrors"
          />
        </div>

        <div v-if="importProgress?.status === 'failed'" class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 p-3">
          <p class="text-sm text-n-ruby-11">
            {{ importError || 'A importação falhou. Verifique o arquivo e tente novamente.' }}
          </p>
        </div>

        <div class="flex justify-end gap-2 pt-2">
          <Button
            label="Nova importação"
            icon="i-lucide-upload"
            color="slate"
            variant="ghost"
            size="sm"
            @click="resetDialog"
          />
          <Button
            label="Fechar"
            color="slate"
            variant="faded"
            size="sm"
            @click="dialogRef?.close()"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>
